import { useManagementForceCancelUnitActivation } from '@apps-common/apis/management/unit-activation'
import {
  useActionUnitActivation,
  useArchiveUnitActivation,
  useCancelUnitActivation,
  useDeleteUnitActivation
} from '@apps-common/apis/portal/unit-activations'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UnitActivationAction } from '@apps-common/config/unit-activation'
import { IAppNavigation } from '@apps-common/hooks/useNavigation'
import { useUnitActivationStatusOptions } from '@apps-common/hooks/useUnitActivationStatusOptions'
import { AUTH_CLAIMS, Authorization } from '@apps-common/lib'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import { ROUTE_EDIT_UNIT_ACTIVATION, ROUTE_UNIT_ACTIVATION_DETAILS } from '@apps-radionet-portal-root/src/config/private-routes'
import { invalidateActivationQueries } from '../../lib/invalidateActivationQueries'
import { ActionButton } from '../UnitActivationActionButton'

interface IProps {
  activation: IActivationServiceRequestModel
  navigation: IAppNavigation
}

export const UnitActivationsTableActions: React.FC<IProps> = ({ activation, navigation }) => {
  const { unitActivationRequestId, status, archived } = activation
  const { navigateForward } = navigation

  const { isProcessing, showActivate, showRetry, showDelete, showArchive, showEdit, canCancel } = useUnitActivationStatusOptions(
    status,
    archived
  )

  const { mutate: actionActivation, isPending: actionActivationIsPending } = useActionUnitActivation(
    true,
    () => invalidateActivationQueries(unitActivationRequestId),
    () => invalidateActivationQueries(unitActivationRequestId)
  )
  const { mutate: archiveActivation, isPending: archiveActivationIsPending } = useArchiveUnitActivation(true, () =>
    invalidateActivationQueries(unitActivationRequestId)
  )
  const { mutate: cancelActivation, isPending: cancelActivationIsPending } = useCancelUnitActivation(true, () =>
    invalidateActivationQueries(unitActivationRequestId)
  )
  const { mutate: deleteActivation, isPending: deleteActivationIsPending } = useDeleteUnitActivation(true, () =>
    invalidateActivationQueries(unitActivationRequestId)
  )
  const { mutate: forceCancelActivation, isPending: forceCancelIsPending } = useManagementForceCancelUnitActivation(true, () =>
    invalidateActivationQueries(unitActivationRequestId)
  )

  const loading =
    actionActivationIsPending ||
    archiveActivationIsPending ||
    cancelActivationIsPending ||
    deleteActivationIsPending ||
    forceCancelIsPending

  return (
    <div className='flex items-center justify-end gap-[8px]'>
      {isProcessing && (
        <Authorization roles={AUTH_CLAIMS.managementAdmin.roles} scopes={AUTH_CLAIMS.managementAdmin.scopes}>
          <ModalButton
            component={ConfirmModal}
            disabled={false}
            classes=''
            componentProps={{
              text: 'Force Cancel',
              description: `Confirm you would like to FORCE cancel this device activation? 
                
                Please note that if the activation is in progress, it may still complete the activation and potentially update this activation afterwards. 
                Please only use this feature if you believe the activation is stuck.`,
              action: ConfirmModalAction.Error
            }}
            confirmCallback={() => forceCancelActivation({ id: unitActivationRequestId })}
            tooltip={{ id: `device-activation-force-cancel-${unitActivationRequestId}`, message: 'Force Cancel' }}
          >
            <ActionButton type='div' action={UnitActivationAction.terminate} className='action-delete' />
          </ModalButton>
        </Authorization>
      )}

      {(showActivate || showRetry) && (
        <>
          <ActionButton
            type='button'
            action={UnitActivationAction.activateNow}
            disabled={loading}
            className='action-activate-now'
            callback={() => actionActivation({ model: activation, action: UnitActivationAction.activateNow })}
            tooltip={{ id: `device-activation-activate-now-${unitActivationRequestId}`, message: showRetry ? 'Retry now' : 'Activate now' }}
          />
          <ActionButton
            type='button'
            action={UnitActivationAction.powerUp}
            disabled={loading}
            className='action-power-up'
            callback={() => actionActivation({ model: activation, action: UnitActivationAction.powerUp })}
            tooltip={{
              id: `device-activation-power-up-${unitActivationRequestId}`,
              message: showRetry ? 'Retry on power up' : 'Activate on power up'
            }}
          />
        </>
      )}

      {showEdit && (
        <ActionButton
          type='button'
          action={UnitActivationAction.edit}
          disabled={loading}
          className='action-edit'
          callback={() =>
            navigateForward({
              routeUrl: ROUTE_EDIT_UNIT_ACTIVATION.buildPath(activation.unitActivationRequestId),
              route: ROUTE_EDIT_UNIT_ACTIVATION
            })
          }
          tooltip={{ id: `device-activation-edit-${unitActivationRequestId}`, message: 'Edit' }}
        />
      )}

      {showDelete && (
        <ModalButton
          component={ConfirmModal}
          disabled={false}
          classes=''
          componentProps={{
            text: 'Delete',
            description: 'Confirm you would like to delete this device activation?',
            action: ConfirmModalAction.Error
          }}
          confirmCallback={() => deleteActivation({ id: unitActivationRequestId })}
          tooltip={{ id: `device-activation-delete-${unitActivationRequestId}`, message: 'Delete' }}
        >
          <ActionButton type='div' action={UnitActivationAction.delete} disabled={loading} className='action-delete' />
        </ModalButton>
      )}

      {showArchive && (
        <ModalButton
          component={ConfirmModal}
          disabled={false}
          classes=''
          componentProps={{
            text: 'Archive',
            description: 'Confirm you would like to archive this device activation?',
            action: ConfirmModalAction.Pending
          }}
          confirmCallback={() => archiveActivation({ id: unitActivationRequestId })}
          tooltip={{ id: `device-activation-archive-${unitActivationRequestId}`, message: 'Archive' }}
        >
          <ActionButton type='div' action={UnitActivationAction.archive} disabled={loading} className='action-archive' />
        </ModalButton>
      )}

      {canCancel && (
        <ModalButton
          component={ConfirmModal}
          disabled={false}
          classes=''
          componentProps={{
            text: 'Cancel',
            description: 'Confirm you would like to cancel this device activation?',
            action: ConfirmModalAction.Warning
          }}
          confirmCallback={() => cancelActivation({ id: unitActivationRequestId })}
          tooltip={{ id: `device-activation-cancel-${unitActivationRequestId}`, message: 'Cancel' }}
        >
          <ActionButton type='div' action={UnitActivationAction.cancel} disabled={loading} className='action-cancel' />
        </ModalButton>
      )}

      <ButtonRoundSm
        type='button'
        loading={loading}
        className='bg-brand text-on-brand'
        callback={() => {
          navigateForward({
            routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(unitActivationRequestId),
            route: ROUTE_UNIT_ACTIVATION_DETAILS
          })
        }}
        tooltip={{ id: `device-activation-details-${unitActivationRequestId}`, message: 'Details' }}
      >
        <commonIcons.arrowAngled />
      </ButtonRoundSm>
    </div>
  )
}
